import {_} from 'lodash';
import * as ko from "knockout";
import {moment} from 'moment';
import {MAX_CONCURRENT_REQUESTS, MAX_RESULTS_PER_PAGE, ObjectTypeId, SPINNER,} from "../../../constants";
import PromiseRequest from "../../../req/PromiseRequest";
import {IMessageData, IObjectData, ISyncField, TSyncfield} from "../../../index";
import {IParams} from "./AbstractDialogOperation";
import pubsub from "../../../pubsub";
import {IComponentDialogParams} from "../../modal/component";
import {
    downloadCSV,
    getObjectDefinition,
    getSourceTranslation,
    getSyncFieldValue,
    IObjectDefinition
} from "../../../util/misc";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {getSearchObjectParams} from "../../../util/request";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {IRequestObject} from "../../../req/index";
import {ISelectSyncField} from "./index";
import {customStoreObservable} from "../../../util/sessionstore";
import {PLEASE_WAIT_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import export_members_csvHtml from './export.members.csv.html?raw';

export let template = export_members_csvHtml;

enum Step {
    Init,
    SelectSyncFields,
    DownloadIds,
    DownloadGroups,
    DownloadCSV,
    Complete,
}

export class viewModel {
    private data: number[] | null;
    private loadError!: number[];
    private loadSuccess!: IObjectData[];
    private readonly Step = Step;
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly numTotal: number;
    private readonly objectDefinition: IObjectDefinition;
    private readonly percentComplete: KnockoutObservable<number | null> = ko.observable(null);
    private readonly promiseRequests: PromiseRequest[] = [];
    private readonly reqSearchObject = new PromiseRequest("searchobject");
    private readonly step: KnockoutObservable<Step | number | null> = ko.observable(null);
    private groupsWithMembers!: IObjectData[];
    private reqGetSyncFields!: IRequestObject;
    private syncfields!: ISyncField[];
    private selectedSyncfields: KnockoutObservableArray<ISelectSyncField> = ko.observableArray<ISelectSyncField>([]);
    private allSyncfields!: ISyncField[];
    private static readonly RegularSyncfields: TSyncfield[] = ['GivenName', 'Surname', 'EmployeeNumber'];

    constructor(private params: IParams) {
        pubsub.on(this, "before.close.dialog", (e: any, dialogData: IComponentDialogParams) => {
            if (dialogData.dialog == this.params.dialog) {
                this.dispose();
            }
        });
        this.data = params.data()?.length ? params.data().map(o => o.id) : null;
        this.numTotal = params.numTotal;
        this.objectDefinition = getObjectDefinition(params.objecttypeid)!;
        this.kosub.subscribe(this.step, this.updateStep.bind(this));
        this.step(Step.Init);
    }

    protected updateStep() {
        switch (this.step()) {
            case Step.Init: {
                this.messageData({message: REQUEST_MESSAGE});
                (this.reqGetSyncFields = this.reqGetSyncFields ?? nsSyncFields.getSyncFields(ObjectTypeId.User))
                    .request
                this.reqGetSyncFields.request
                    .then(((data: ISyncField[]) => {
                            this.allSyncfields = data;
                            const d: ISelectSyncField[] = data.map(sf => {
                                const sessionStoreKey = this.getSessionStoreKey(sf.name);
                                return {
                                    syncfield: sf,
                                    selected: customStoreObservable(sessionStoreKey, sf.core),
                                }
                            });
                            d.unshift({
                                syncfield: {name: "Id"} as any,
                                selected: customStoreObservable(this.getSessionStoreKey("id"), true),
                            });
                            this.selectedSyncfields(d);
                            this.messageData(null);
                            this.step(Step.SelectSyncFields);
                        })
                    )
                    .catch(() => {
                        this.step(Step.Complete);
                    });
                break;
            }

            case Step.DownloadIds: {
                // do we have object ids already?
                if (this.data?.length) {
                    this.step(Step.DownloadGroups);
                } else {
                    // download the ids
                    this.messageData({message: REQUEST_MESSAGE});
                    this.reqSearchObject
                        .request(this.params.searchObjectParams)
                        .then((data: number[]) => {
                            this.data = data;
                            this.step(Step.DownloadGroups);
                        });
                }
                break;
            }

            case Step.DownloadGroups: {
                this.loadSuccess = [];
                this.loadError = [];

                const ids = (this.data ?? []).slice();

                const updatePercent = () => {
                    this.percentComplete((this.loadSuccess.length + this.loadError.length) / this.numTotal);
                    const percentComplete = this.percentComplete();
                    const percent = percentComplete != null ? (percentComplete * 100).toFixed(1) : '0';
                    this.messageData({message: `((loading data|td)) ${percent}% ${SPINNER}`});
                }

                new Promise((resolve: (value: unknown) => void) => {
                    this.messageData({message: REQUEST_MESSAGE});
                    const doNext = () => {
                        const id = ids.shift();
                        const p: PromiseRequest = new PromiseRequest('getobject');
                        this.promiseRequests.push(p);
                        let group: IObjectData;
                        return p
                            .request({id: id, manual: this.params.searchObjectParams.manualobjects})
                            .then((data: IObjectData[]) => {
                                group = _.cloneDeep(data[0]);
                                const promiseRequest = new PromiseRequest('searchgroupmember');
                                this.promiseRequests.push(promiseRequest);
                                return promiseRequest
                                    .request(getSearchObjectParams({
                                        objectid: group.id,
                                        resultsprpage: MAX_RESULTS_PER_PAGE,
                                    }))
                                    .then((data: IObjectData[]) => {
                                        const memberIds: number[] = data.map(o => o.id);
                                        const promiseRequest = new PromiseRequest("searchobject")
                                        this.promiseRequests.push(promiseRequest);
                                        const jruleset = nsRuleset.generateRuleset({
                                            rules: [nsRuleset.generateRule({
                                                attribute: 'Id',
                                                argumentoperator: 'Er lik en av',
                                                argument: memberIds.join(','),
                                            })]
                                        });
                                        return promiseRequest.request(getSearchObjectParams({
                                            jruleset: JSON.stringify(jruleset),
                                            resultsprpage: MAX_RESULTS_PER_PAGE,
                                        }))
                                    })
                                    .then((data: IObjectData[]) => {
                                        group.members = data;
                                        this.loadSuccess.push(group);
                                    })
                            })
                            .then(() => {
                                updatePercent();
                                if (ids.length) {
                                    doNext();
                                } else if ((this.loadSuccess.length + this.loadError.length) == this.numTotal) {
                                    resolve(null);
                                }
                            })
                            .catch((reason: string) => {
                                this.loadError.push(id!);
                                updatePercent();
                                if (ids.length) {
                                    doNext();
                                } else if ((this.loadSuccess.length + this.loadError.length) == this.numTotal) {
                                    resolve(null);
                                }
                            });
                    }

                    for (let i = 0; i < Math.min(this.numTotal, MAX_CONCURRENT_REQUESTS); i++) {
                        doNext();
                    }
                })
                    .then(() => {
                        this.step(Step.DownloadCSV)
                    })

                break;
            }

            case Step.DownloadCSV: {
                this.messageData({message: PLEASE_WAIT_MESSAGE});
                const groupKeys: TSyncfield[] = ['Name', 'SourceId', 'Description'];
                const memberKeys: TSyncfield[] = this.selectedSyncfields().filter(s => s.selected()).map(s => s.syncfield.name as TSyncfield);
                const csv = ([] as TSyncfield[]).concat(groupKeys, memberKeys)
                        .map(key => `"${key}"`)
                        .join(';') + "\n" +
                    this.loadSuccess.map((group: IObjectData) => {
                        const retGroup = groupKeys
                            .map((key: TSyncfield) => {
                                const val = getSyncFieldValue(key, group.syncfields)
                                return val ? val.replace(/"/gm, '""') : "";
                            })
                            .map(val => `"${val}"`)
                            .join(";");
                        const retMembers = group.members.map((user: IObjectData) => {
                            return memberKeys
                                .map((key: TSyncfield) => {
                                    const val = getSyncFieldValue(key, user.syncfields);
                                    return val ? val.replace(/"/gm, '""') : "";
                                })
                                .map(val => `"${val}"`)
                                .join(";");
                        });
                        return retMembers.map((user: string) => `${retGroup};${user}`).join('\n');
                    }).filter(Boolean).join("\n");
                downloadCSV(csv, this.getFileName());
                this.step(Step.Complete);
                break;
            }

            case Step.Complete:
                this.messageData({message: `((download completed))<br><br>((note: file may contain personal data))`})
                break

        }
    }

    /*******************************************************************************************************************
     * View interaction
     ******************************************************************************************************************/

    onContinueClick() {
        this.step((this.step() ?? 0) + 1);
    }

    /*******************************************************************************************************************
     * View helpers / computed properties
     ******************************************************************************************************************/

    isStep(...steps: number[]): boolean {
        return steps.some((step: Step) => step == this.step());
    }

    selectAllSyncFields = ko.computed({
        read: () => {
            return this.selectedSyncfields().every((item: ISelectSyncField) => item.selected());
        },
        write: (val: boolean) => {
            this.selectedSyncfields().forEach((item: ISelectSyncField) => item.selected(val));
        }
    });

    private sortedSyncFields = ko.pureComputed(() => {
        return _.sortBy(this.selectedSyncfields(), (ssf: ISelectSyncField) => getSourceTranslation(ssf.syncfield));
    });

    isDownloadable = ko.pureComputed(() => this.selectedSyncfields().some((item: ISelectSyncField) => item.selected()));

    /*******************************************************************************************************************
     * Misc.
     ******************************************************************************************************************/

    protected getFileName(): string {
        let ret: string = "eksport_av_gruppemedlemmer_";
        ret += moment().format('L').replace(/\//g, '-')
            + ".csv";
        return ret;
    }

    private getSessionStoreKey(name: string): string {
        return "syncfield." + ObjectTypeId.User + "." + name;
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.reqSearchObject.abort();
        this.promiseRequests.forEach((p: PromiseRequest) => p.abort());
    }

}
